/**
 * Static UI strings per supported language (same mechanism as kys-platform's
 * uiStrings). Spanish (`es`) is the source and the fallback for any missing
 * key. Use `{name}` placeholders and pass values via the `params` argument.
 */
import type { Locale } from '@/types/api.types';
import { EVENT_STRINGS } from './uiStrings.events';

export type UiStrings = Record<string, string>;

export const DEFAULT_LANGUAGE: Locale = 'es';
export const SUPPORTED_LANGUAGES: { code: Locale; nativeName: string }[] = [
  { code: 'es', nativeName: 'Español' },
  { code: 'fr', nativeName: 'Français' },
  { code: 'en', nativeName: 'English' },
];
export const SUPPORTED_LANGUAGE_CODES: Locale[] = SUPPORTED_LANGUAGES.map((l) => l.code);
export const UI_LOCALES: Record<Locale, string> = { es: 'es-PE', fr: 'fr-FR', en: 'en-GB' };

const es: UiStrings = {
  // App chrome
  'app.title': 'Jubilación Segura',
  'app.subtitle': 'Te escucha',
  'app.tagline': 'Proyecto de reforestación y agroforestería',
  'app.language': 'Idioma',
  'app.footer': 'Ingresa tus consultas, dudas, sugerencias, quejas o reclamos relacionados al Proyecto Jubilación Segura.',
  'app.back': 'Atrás',
  'app.next': 'Siguiente',
  'app.submit': 'Enviar',
  'app.cancel': 'Cancelar',
  'app.loading': 'Cargando…',
  'app.optional': 'opcional',
  'app.error': 'Ocurrió un error. Intenta nuevamente.',
  'app.retry': 'Reintentar',
  'nav.newRequest': 'Nueva solicitud',
  'nav.track': 'Seguimiento',
  'nav.staff': 'Equipo del proyecto',
  'nav.privacy': '¿Cómo protegemos tus datos?',

  // Home
  'home.heading': '¿Tienes una consulta, duda, sugerencia, queja o reclamo?',
  'home.intro':
    'Este canal te permite comunicarte con el equipo del Proyecto Jubilación Segura. Puedes hacerlo de forma anónima o dejando tus datos para recibir una respuesta directa.',
  'home.start': 'Iniciar solicitud',
  'home.trackCta': 'Ya tengo un código de seguimiento',

  // Wizard steps
  'form.steps.type': 'Tipo de registro',
  'form.steps.requester': 'Tus datos',
  'form.steps.request': 'Tu solicitud',
  'form.steps.location': 'Ubicación',
  'form.steps.review': 'Revisión',
  'form.type.heading': 'Selecciona una opción para tu solicitud',
  'form.type.anonymous.title': 'Registro anónimo',
  'form.type.anonymous.desc':
    'No es necesario registrar tus datos personales (nombre, teléfono, etc.). La respuesta será compartida de forma general a través de las actividades del proyecto. No podrás recibir una respuesta directa y formal.',
  'form.type.identified.title': 'Registro no anónimo',
  'form.type.identified.desc':
    'Registras tus datos personales (nombre, teléfono, etc.). El equipo del proyecto te contactará durante la evaluación y/o para darte una respuesta directa y formal.',
  'form.requester.heading': 'Datos del solicitante',
  'form.requester.notice':
    'Esta información será usada únicamente para gestionar la respuesta. No será compartida con otras personas o instituciones.',
  'form.requester.fullName': 'Apellidos y nombres / Razón social',
  'form.requester.gender': 'Género',
  'form.requester.gender.M': 'Hombre',
  'form.requester.gender.F': 'Mujer',
  'form.requester.gender.UNSPECIFIED': 'Prefiero no especificar',
  'form.requester.idDocumentType': 'Tipo de documento',
  'form.requester.id.DNI': 'DNI',
  'form.requester.id.PASSPORT': 'Pasaporte',
  'form.requester.id.RUC': 'RUC (persona jurídica)',
  'form.requester.id.OTHER': 'Otro',
  'form.requester.idDocumentNumber': 'Número de documento',
  'form.requester.idHint.DNI': '8 dígitos',
  'form.requester.idHint.RUC': '11 dígitos',
  'form.requester.idHint.PASSPORT': '6 a 12 caracteres',
  'form.requester.idHint.OTHER': '',
  'form.requester.birthDate': 'Fecha de nacimiento',
  'form.requester.birthHint': 'Debes ser mayor de 18 años.',
  'form.requester.email': 'Correo electrónico',
  'form.requester.phone': 'Teléfono o celular de contacto',
  'form.requester.province': 'Provincia donde vives',
  'form.requester.address': 'Dirección o ubicación',
  'form.request.heading': 'Descripción de tu solicitud',
  'form.request.category': 'Tipo de solicitud',
  'form.request.description': 'Describe tu consulta, duda, sugerencia, queja o reclamo',
  'form.request.descriptionHint': 'Mínimo 10 caracteres. Sé lo más concreto posible: qué pasó, cuándo, dónde.',
  'form.request.proposedSolution': 'Para quejas o reclamos: ¿qué solución propones?',
  'form.request.attachmentHint': 'JPG, PNG o PDF, máximo {max} MB. Podrás adjuntarlo después de enviar.',
  'form.location.heading': 'Ubicación relacionada a tu solicitud',
  'form.location.province': 'Provincia del área del proyecto',
  'form.location.district': 'Distrito',
  'form.location.sector': 'Sector, caserío o comunidad',
  'form.location.noArea': 'No tiene área específica',
  'form.review.heading': 'Revisa tu solicitud antes de enviar',
  'form.review.anonymousBadge': 'Anónimo',
  'form.review.identifiedBadge': 'Con datos de contacto',
  'form.review.consentTitle': 'Tratamiento de datos personales',
  'form.review.consentText':
    'He leído la información sobre el tratamiento de mis datos personales y acepto que el equipo del Proyecto Jubilación Segura los utilice únicamente para gestionar y responder mi solicitud. Mis datos se conservarán de forma cifrada y serán eliminados una vez transcurrido el plazo de conservación.',
  'form.review.consentAnonymousText':
    'Entiendo que mi solicitud es anónima: no se registra ningún dato personal y la respuesta se compartirá de forma general.',
  'form.review.readNotice': 'Leer la información completa',
  'form.review.edit': 'Editar',

  // Validation
  'error.required': 'Este campo es obligatorio.',
  'error.tooShort': 'Demasiado corto.',
  'error.tooLong': 'Demasiado largo.',
  'error.invalidEmail': 'Correo electrónico no válido.',
  'error.invalidPhone': 'Teléfono no válido.',
  'error.invalidIdDocument': 'Formato de documento no válido.',
  'error.mustBeAdult': 'Debes ser mayor de 18 años.',
  'error.consent': 'Debes aceptar para continuar.',
  'error.RATE_LIMITED': 'Has enviado demasiadas solicitudes. Intenta más tarde.',
  'error.UNSUPPORTED_FILE_TYPE': 'Tipo de archivo no permitido.',
  'error.FILE_TOO_LARGE': 'El archivo es demasiado grande.',
  'error.FILE_CONTENT_MISMATCH': 'El contenido del archivo no corresponde a su tipo.',
  'error.UPLOAD_WINDOW_CLOSED': 'Ya no es posible adjuntar archivos a esta solicitud.',
  'error.TOO_MANY_ATTACHMENTS': 'Se alcanzó el máximo de archivos adjuntos.',
  'error.CONSENT_VERSION_OUTDATED': 'La información de privacidad fue actualizada. Vuelve a aceptarla.',

  // Success
  'success.heading': '¡Gracias por comunicarte con el Proyecto Jubilación Segura!',
  'success.codeLabel': 'Tu código de seguimiento',
  'success.keepCode': 'Guarda este código. Es la única forma de consultar el estado de tu solicitud.',
  'success.copy': 'Copiar código',
  'success.copied': 'Copiado',
  'success.attachNow': 'Adjuntar una foto o documento',
  'success.attachDone': 'Archivo adjuntado.',
  'success.track': 'Ver el seguimiento',
  'success.another': 'Enviar otra solicitud',

  // Tracking
  'track.heading': 'Seguimiento de tu solicitud',
  'track.codePlaceholder': 'JS-XXXX-XXXX',
  'track.search': 'Consultar',
  'track.notFound': 'No encontramos una solicitud con ese código.',
  'track.invalidCode': 'El código debe tener el formato JS-XXXX-XXXX.',
  'track.submittedOn': 'Enviada el',
  'track.updatedOn': 'Última actualización',
  'track.timeline': 'Historial',
  'status.RECEIVED': 'Recibida',
  'status.IN_REVIEW': 'En evaluación',
  'status.ANSWERED': 'Respondida',
  'status.CLOSED': 'Cerrada',
  'status.REJECTED': 'No procede',
  'statusHelp.RECEIVED': 'Tu solicitud ha sido registrada. El equipo la revisará pronto.',
  'statusHelp.IN_REVIEW': 'El equipo del proyecto está evaluando tu solicitud.',
  'statusHelp.ANSWERED': 'Se ha emitido una respuesta.',
  'statusHelp.CLOSED': 'La solicitud ha sido cerrada.',
  'statusHelp.REJECTED': 'La solicitud no procede. Revisa el motivo indicado.',
  'category.CONSULTA': 'Consulta',
  'category.DUDA': 'Duda',
  'category.SUGERENCIA': 'Sugerencia',
  'category.QUEJA': 'Queja',
  'category.RECLAMO': 'Reclamo',
  'event.CREATED': 'Solicitud recibida',
  'event.STATUS_CHANGED': 'Cambio de estado',
  'event.COMMENT': 'Mensaje del equipo',
  'event.ANSWER_SENT': 'Respuesta',
  'event.ATTACHMENT_ADDED': 'Archivo adjuntado',
  'event.DATA_DELETION_REQUESTED': 'Solicitud de eliminación de datos registrada',
  'event.PII_PURGED': 'Datos personales eliminados',
  'track.deletion.title': 'Eliminar mis datos personales',
  'track.deletion.text':
    'Si dejaste tus datos y deseas que sean eliminados, ingresa tu número de documento para confirmar tu identidad. El equipo procesará la solicitud en un plazo máximo de 30 días.',
  'track.deletion.button': 'Solicitar eliminación',
  'track.deletion.done': 'Solicitud registrada. Si los datos coinciden, serán eliminados en un plazo máximo de 30 días.',

  // Privacy notice
  'privacy.heading': 'Información sobre el tratamiento de datos personales',
  'privacy.who': 'Responsable: PUR, en el marco del Proyecto Jubilación Segura.',
  'privacy.why': 'Finalidad: recibir, evaluar y responder tu consulta, duda, sugerencia, queja o reclamo.',
  'privacy.what':
    'Datos: solo los que ingreses en el formulario (nombre, documento de identidad, fecha de nacimiento, contacto, ubicación) y los archivos que adjuntes. El registro anónimo no recoge ningún dato personal.',
  'privacy.how':
    'Protección: tus datos se almacenan cifrados en servidores de Microsoft Azure (Unión Europea). Solo el equipo autorizado del proyecto puede acceder a ellos y cada acceso queda registrado.',
  'privacy.howLong': 'Conservación: tus datos personales se eliminan {days} días después del cierre de tu solicitud.',
  'privacy.rights':
    'Derechos: puedes solicitar el acceso, rectificación o eliminación de tus datos desde la página de seguimiento con tu código y número de documento, o escribiendo al correo del proyecto.',
  'privacy.law': 'Base legal: tu consentimiento (Ley N° 29733 de Protección de Datos Personales del Perú y RGPD).',

  // Admin
  'admin.title': 'Back-office',
  'admin.signIn': 'Iniciar sesión con Microsoft',
  'admin.signInSub': 'Acceso reservado al equipo del proyecto',
  'admin.devLogin': 'Acceso de desarrollo (sin Microsoft)',
  'admin.signOut': 'Cerrar sesión',
  'admin.forbidden': 'Tu cuenta no tiene acceso a esta aplicación.',
  'admin.dashboard': 'Panel',
  'admin.requests': 'Solicitudes',
  'admin.all': 'Todos',
  'admin.search': 'Buscar (código o texto)',
  'admin.anonymousOnly': 'Solo anónimas',
  'admin.identifiedOnly': 'Solo con datos',
  'admin.col.code': 'Código',
  'admin.col.date': 'Fecha',
  'admin.col.category': 'Tipo',
  'admin.col.province': 'Provincia',
  'admin.col.status': 'Estado',
  'admin.col.type': 'Registro',
  'admin.empty': 'No hay solicitudes con estos filtros.',
  'admin.detail': 'Detalle de la solicitud',
  'admin.requesterData': 'Datos del solicitante',
  'admin.piiWarning': 'Datos personales — este acceso queda registrado en el historial de auditoría.',
  'admin.purged': 'Datos personales eliminados el {date}.',
  'admin.attachments': 'Archivos adjuntos',
  'admin.open': 'Abrir',
  'admin.timeline': 'Historial',
  'admin.publicEvent': 'Visible para el solicitante',
  'admin.changeStatus': 'Cambiar estado',
  'admin.addComment': 'Agregar mensaje',
  'admin.message': 'Mensaje',
  'admin.makePublic': 'Mostrar al solicitante en la página de seguimiento',
  'admin.save': 'Guardar',
  'admin.saved': 'Guardado',
  'admin.stats.total': 'Total',
  'admin.stats.byStatus': 'Por estado',
  'admin.stats.byCategory': 'Por tipo',
  'admin.stats.byProvince': 'Por provincia',
  'admin.stats.last30': 'Últimos 30 días',
  'admin.rejectionReason': 'Indica el motivo (visible para el solicitante).',
};

const fr: UiStrings = {
  'app.title': 'Jubilación Segura',
  'app.subtitle': 'À votre écoute',
  'app.tagline': "Projet de reforestation et d'agroforesterie",
  'app.language': 'Langue',
  'app.footer': 'Déposez vos questions, doutes, suggestions, plaintes ou réclamations concernant le projet Jubilación Segura.',
  'app.back': 'Retour',
  'app.next': 'Suivant',
  'app.submit': 'Envoyer',
  'app.cancel': 'Annuler',
  'app.loading': 'Chargement…',
  'app.optional': 'facultatif',
  'app.error': 'Une erreur est survenue. Réessayez.',
  'app.retry': 'Réessayer',
  'nav.newRequest': 'Nouvelle demande',
  'nav.track': 'Suivi',
  'nav.staff': 'Équipe projet',
  'nav.privacy': 'Comment protégeons-nous vos données ?',
  'home.heading': 'Vous avez une question, un doute, une suggestion, une plainte ou une réclamation ?',
  'home.intro':
    "Ce canal vous permet de contacter l'équipe du projet Jubilación Segura, de façon anonyme ou en laissant vos coordonnées pour recevoir une réponse directe.",
  'home.start': 'Commencer une demande',
  'home.trackCta': "J'ai déjà un code de suivi",
  'form.steps.type': "Type d'enregistrement",
  'form.steps.requester': 'Vos coordonnées',
  'form.steps.request': 'Votre demande',
  'form.steps.location': 'Localisation',
  'form.steps.review': 'Vérification',
  'form.type.heading': 'Choisissez une option pour votre demande',
  'form.type.anonymous.title': 'Enregistrement anonyme',
  'form.type.anonymous.desc':
    "Aucune donnée personnelle (nom, téléphone…) n'est enregistrée. La réponse sera partagée de façon générale lors des activités du projet. Vous ne recevrez pas de réponse directe et formelle.",
  'form.type.identified.title': 'Enregistrement non anonyme',
  'form.type.identified.desc':
    "Vous enregistrez vos données personnelles (nom, téléphone…). L'équipe du projet vous contactera pendant l'évaluation et/ou pour vous apporter une réponse directe et formelle.",
  'form.requester.heading': 'Coordonnées du demandeur',
  'form.requester.notice':
    'Ces informations servent uniquement à traiter votre demande. Elles ne seront partagées avec aucune autre personne ou institution.',
  'form.requester.fullName': 'Nom et prénoms / Raison sociale',
  'form.requester.gender': 'Genre',
  'form.requester.gender.M': 'Homme',
  'form.requester.gender.F': 'Femme',
  'form.requester.gender.UNSPECIFIED': 'Je préfère ne pas préciser',
  'form.requester.idDocumentType': 'Type de document',
  'form.requester.id.DNI': 'DNI',
  'form.requester.id.PASSPORT': 'Passeport',
  'form.requester.id.RUC': 'RUC (personne morale)',
  'form.requester.id.OTHER': 'Autre',
  'form.requester.idDocumentNumber': 'Numéro de document',
  'form.requester.idHint.DNI': '8 chiffres',
  'form.requester.idHint.RUC': '11 chiffres',
  'form.requester.idHint.PASSPORT': '6 à 12 caractères',
  'form.requester.idHint.OTHER': '',
  'form.requester.birthDate': 'Date de naissance',
  'form.requester.birthHint': 'Vous devez avoir 18 ans ou plus.',
  'form.requester.email': 'Adresse e-mail',
  'form.requester.phone': 'Téléphone / portable',
  'form.requester.province': 'Province de résidence',
  'form.requester.address': 'Adresse ou localisation',
  'form.request.heading': 'Description de votre demande',
  'form.request.category': 'Type de demande',
  'form.request.description': 'Décrivez votre question, doute, suggestion, plainte ou réclamation',
  'form.request.descriptionHint': '10 caractères minimum. Soyez concret : quoi, quand, où.',
  'form.request.proposedSolution': 'Pour une plainte ou réclamation : quelle solution proposez-vous ?',
  'form.request.attachmentHint': "JPG, PNG ou PDF, {max} Mo maximum. Vous pourrez le joindre après l'envoi.",
  'form.location.heading': 'Lieu concerné par votre demande',
  'form.location.province': 'Province de la zone du projet',
  'form.location.district': 'District',
  'form.location.sector': 'Secteur, hameau ou communauté',
  'form.location.noArea': 'Pas de zone spécifique',
  'form.review.heading': "Vérifiez votre demande avant l'envoi",
  'form.review.anonymousBadge': 'Anonyme',
  'form.review.identifiedBadge': 'Avec coordonnées',
  'form.review.consentTitle': 'Traitement des données personnelles',
  'form.review.consentText':
    "J'ai lu l'information sur le traitement de mes données personnelles et j'accepte que l'équipe du projet Jubilación Segura les utilise uniquement pour traiter et répondre à ma demande. Mes données sont conservées chiffrées et supprimées à l'issue du délai de conservation.",
  'form.review.consentAnonymousText':
    "Je comprends que ma demande est anonyme : aucune donnée personnelle n'est enregistrée et la réponse sera partagée de façon générale.",
  'form.review.readNotice': "Lire l'information complète",
  'form.review.edit': 'Modifier',
  'error.required': 'Ce champ est obligatoire.',
  'error.tooShort': 'Trop court.',
  'error.tooLong': 'Trop long.',
  'error.invalidEmail': 'Adresse e-mail invalide.',
  'error.invalidPhone': 'Numéro de téléphone invalide.',
  'error.invalidIdDocument': 'Format de document invalide.',
  'error.mustBeAdult': 'Vous devez avoir 18 ans ou plus.',
  'error.consent': 'Vous devez accepter pour continuer.',
  'error.RATE_LIMITED': 'Trop de demandes envoyées. Réessayez plus tard.',
  'error.UNSUPPORTED_FILE_TYPE': 'Type de fichier non autorisé.',
  'error.FILE_TOO_LARGE': 'Fichier trop volumineux.',
  'error.FILE_CONTENT_MISMATCH': 'Le contenu du fichier ne correspond pas à son type.',
  'error.UPLOAD_WINDOW_CLOSED': "Il n'est plus possible de joindre des fichiers à cette demande.",
  'error.TOO_MANY_ATTACHMENTS': 'Nombre maximum de pièces jointes atteint.',
  'error.CONSENT_VERSION_OUTDATED': "L'information sur la vie privée a été mise à jour. Veuillez l'accepter de nouveau.",
  'success.heading': "Merci d'avoir contacté le projet Jubilación Segura !",
  'success.codeLabel': 'Votre code de suivi',
  'success.keepCode': "Conservez ce code : c'est le seul moyen de consulter l'état de votre demande.",
  'success.copy': 'Copier le code',
  'success.copied': 'Copié',
  'success.attachNow': 'Joindre une photo ou un document',
  'success.attachDone': 'Fichier joint.',
  'success.track': 'Voir le suivi',
  'success.another': 'Envoyer une autre demande',
  'track.heading': 'Suivi de votre demande',
  'track.codePlaceholder': 'JS-XXXX-XXXX',
  'track.search': 'Consulter',
  'track.notFound': 'Aucune demande trouvée avec ce code.',
  'track.invalidCode': 'Le code doit être au format JS-XXXX-XXXX.',
  'track.submittedOn': 'Envoyée le',
  'track.updatedOn': 'Dernière mise à jour',
  'track.timeline': 'Historique',
  'status.RECEIVED': 'Reçue',
  'status.IN_REVIEW': "En cours d'évaluation",
  'status.ANSWERED': 'Répondue',
  'status.CLOSED': 'Clôturée',
  'status.REJECTED': 'Non recevable',
  'statusHelp.RECEIVED': "Votre demande est enregistrée. L'équipe l'examinera prochainement.",
  'statusHelp.IN_REVIEW': "L'équipe du projet évalue votre demande.",
  'statusHelp.ANSWERED': 'Une réponse a été apportée.',
  'statusHelp.CLOSED': 'La demande est clôturée.',
  'statusHelp.REJECTED': "La demande n'est pas recevable. Consultez le motif indiqué.",
  'category.CONSULTA': 'Question',
  'category.DUDA': 'Doute',
  'category.SUGERENCIA': 'Suggestion',
  'category.QUEJA': 'Plainte',
  'category.RECLAMO': 'Réclamation',
  'event.CREATED': 'Demande reçue',
  'event.STATUS_CHANGED': "Changement d'état",
  'event.COMMENT': "Message de l'équipe",
  'event.ANSWER_SENT': 'Réponse',
  'event.ATTACHMENT_ADDED': 'Fichier joint',
  'event.DATA_DELETION_REQUESTED': 'Demande de suppression des données enregistrée',
  'event.PII_PURGED': 'Données personnelles supprimées',
  'track.deletion.title': 'Supprimer mes données personnelles',
  'track.deletion.text':
    "Si vous avez laissé vos coordonnées et souhaitez leur suppression, saisissez votre numéro de document pour confirmer votre identité. L'équipe traitera la demande sous 30 jours.",
  'track.deletion.button': 'Demander la suppression',
  'track.deletion.done': 'Demande enregistrée. Si les données correspondent, elles seront supprimées sous 30 jours.',
  'privacy.heading': 'Information sur le traitement des données personnelles',
  'privacy.who': 'Responsable : PUR, dans le cadre du projet Jubilación Segura.',
  'privacy.why': 'Finalité : recevoir, évaluer et répondre à votre question, doute, suggestion, plainte ou réclamation.',
  'privacy.what':
    "Données : uniquement celles saisies dans le formulaire (nom, document d'identité, date de naissance, contact, localisation) et les fichiers joints. L'enregistrement anonyme ne collecte aucune donnée personnelle.",
  'privacy.how':
    "Protection : vos données sont stockées chiffrées sur des serveurs Microsoft Azure (Union européenne). Seule l'équipe autorisée du projet y accède et chaque accès est journalisé.",
  'privacy.howLong': 'Conservation : vos données personnelles sont supprimées {days} jours après la clôture de votre demande.',
  'privacy.rights':
    "Droits : vous pouvez demander l'accès, la rectification ou la suppression de vos données depuis la page de suivi avec votre code et votre numéro de document, ou par e-mail auprès du projet.",
  'privacy.law': 'Base légale : votre consentement (Loi péruvienne N° 29733 sur la protection des données personnelles et RGPD).',
  'admin.title': 'Back-office',
  'admin.signIn': 'Se connecter avec Microsoft',
  'admin.signInSub': "Accès réservé à l'équipe du projet",
  'admin.devLogin': 'Accès de développement (sans Microsoft)',
  'admin.signOut': 'Se déconnecter',
  'admin.forbidden': "Votre compte n'a pas accès à cette application.",
  'admin.dashboard': 'Tableau de bord',
  'admin.requests': 'Demandes',
  'admin.all': 'Tous',
  'admin.search': 'Rechercher (code ou texte)',
  'admin.anonymousOnly': 'Anonymes uniquement',
  'admin.identifiedOnly': 'Avec coordonnées uniquement',
  'admin.col.code': 'Code',
  'admin.col.date': 'Date',
  'admin.col.category': 'Type',
  'admin.col.province': 'Province',
  'admin.col.status': 'État',
  'admin.col.type': 'Enregistrement',
  'admin.empty': 'Aucune demande pour ces filtres.',
  'admin.detail': 'Détail de la demande',
  'admin.requesterData': 'Coordonnées du demandeur',
  'admin.piiWarning': "Données personnelles — cet accès est journalisé dans l'audit.",
  'admin.purged': 'Données personnelles supprimées le {date}.',
  'admin.attachments': 'Pièces jointes',
  'admin.open': 'Ouvrir',
  'admin.timeline': 'Historique',
  'admin.publicEvent': 'Visible par le demandeur',
  'admin.changeStatus': "Changer l'état",
  'admin.addComment': 'Ajouter un message',
  'admin.message': 'Message',
  'admin.makePublic': 'Afficher au demandeur sur la page de suivi',
  'admin.save': 'Enregistrer',
  'admin.saved': 'Enregistré',
  'admin.stats.total': 'Total',
  'admin.stats.byStatus': 'Par état',
  'admin.stats.byCategory': 'Par type',
  'admin.stats.byProvince': 'Par province',
  'admin.stats.last30': '30 derniers jours',
  'admin.rejectionReason': 'Indiquez le motif (visible par le demandeur).',
};

const en: UiStrings = {
  'app.title': 'Jubilación Segura',
  'app.subtitle': 'We listen',
  'app.tagline': 'Reforestation and agroforestry project',
  'app.language': 'Language',
  'app.footer': 'Submit your questions, doubts, suggestions, complaints or grievances about the Jubilación Segura project.',
  'app.back': 'Back',
  'app.next': 'Next',
  'app.submit': 'Submit',
  'app.cancel': 'Cancel',
  'app.loading': 'Loading…',
  'app.optional': 'optional',
  'app.error': 'Something went wrong. Please try again.',
  'app.retry': 'Retry',
  'nav.newRequest': 'New request',
  'nav.track': 'Track',
  'nav.staff': 'Project team',
  'nav.privacy': 'How do we protect your data?',
  'home.heading': 'Do you have a question, doubt, suggestion, complaint or grievance?',
  'home.intro':
    'This channel lets you reach the Jubilación Segura project team, either anonymously or leaving your contact details to receive a direct answer.',
  'home.start': 'Start a request',
  'home.trackCta': 'I already have a tracking code',
  'form.steps.type': 'Registration type',
  'form.steps.requester': 'Your details',
  'form.steps.request': 'Your request',
  'form.steps.location': 'Location',
  'form.steps.review': 'Review',
  'form.type.heading': 'Choose an option for your request',
  'form.type.anonymous.title': 'Anonymous registration',
  'form.type.anonymous.desc':
    'No personal data (name, phone, etc.) is recorded. The answer will be shared in general terms through project activities. You will not receive a direct, formal answer.',
  'form.type.identified.title': 'Non-anonymous registration',
  'form.type.identified.desc':
    'You provide your personal data (name, phone, etc.). The project team will contact you during the evaluation and/or to give you a direct, formal answer.',
  'form.requester.heading': 'Requester details',
  'form.requester.notice': 'This information is used only to handle your request. It will not be shared with any other person or institution.',
  'form.requester.fullName': 'Full name / Company name',
  'form.requester.gender': 'Gender',
  'form.requester.gender.M': 'Male',
  'form.requester.gender.F': 'Female',
  'form.requester.gender.UNSPECIFIED': 'Prefer not to say',
  'form.requester.idDocumentType': 'Document type',
  'form.requester.id.DNI': 'DNI',
  'form.requester.id.PASSPORT': 'Passport',
  'form.requester.id.RUC': 'RUC (legal entity)',
  'form.requester.id.OTHER': 'Other',
  'form.requester.idDocumentNumber': 'Document number',
  'form.requester.idHint.DNI': '8 digits',
  'form.requester.idHint.RUC': '11 digits',
  'form.requester.idHint.PASSPORT': '6 to 12 characters',
  'form.requester.idHint.OTHER': '',
  'form.requester.birthDate': 'Date of birth',
  'form.requester.birthHint': 'You must be 18 or older.',
  'form.requester.email': 'Email address',
  'form.requester.phone': 'Phone / mobile',
  'form.requester.province': 'Province where you live',
  'form.requester.address': 'Address or location',
  'form.request.heading': 'Describe your request',
  'form.request.category': 'Request type',
  'form.request.description': 'Describe your question, doubt, suggestion, complaint or grievance',
  'form.request.descriptionHint': 'At least 10 characters. Be specific: what, when, where.',
  'form.request.proposedSolution': 'For complaints or grievances: what solution do you propose?',
  'form.request.attachmentHint': 'JPG, PNG or PDF, up to {max} MB. You can attach it after submitting.',
  'form.location.heading': 'Location related to your request',
  'form.location.province': 'Province of the project area',
  'form.location.district': 'District',
  'form.location.sector': 'Sector, hamlet or community',
  'form.location.noArea': 'No specific area',
  'form.review.heading': 'Review your request before submitting',
  'form.review.anonymousBadge': 'Anonymous',
  'form.review.identifiedBadge': 'With contact details',
  'form.review.consentTitle': 'Personal data processing',
  'form.review.consentText':
    'I have read the information about the processing of my personal data and agree that the Jubilación Segura project team uses it only to handle and answer my request. My data is stored encrypted and deleted after the retention period.',
  'form.review.consentAnonymousText':
    'I understand my request is anonymous: no personal data is recorded and the answer will be shared in general terms.',
  'form.review.readNotice': 'Read the full notice',
  'form.review.edit': 'Edit',
  'error.required': 'This field is required.',
  'error.tooShort': 'Too short.',
  'error.tooLong': 'Too long.',
  'error.invalidEmail': 'Invalid email address.',
  'error.invalidPhone': 'Invalid phone number.',
  'error.invalidIdDocument': 'Invalid document format.',
  'error.mustBeAdult': 'You must be 18 or older.',
  'error.consent': 'You must accept to continue.',
  'error.RATE_LIMITED': 'Too many requests sent. Please try again later.',
  'error.UNSUPPORTED_FILE_TYPE': 'File type not allowed.',
  'error.FILE_TOO_LARGE': 'File is too large.',
  'error.FILE_CONTENT_MISMATCH': 'The file content does not match its type.',
  'error.UPLOAD_WINDOW_CLOSED': 'Files can no longer be attached to this request.',
  'error.TOO_MANY_ATTACHMENTS': 'Maximum number of attachments reached.',
  'error.CONSENT_VERSION_OUTDATED': 'The privacy notice was updated. Please accept it again.',
  'success.heading': 'Thank you for contacting the Jubilación Segura project!',
  'success.codeLabel': 'Your tracking code',
  'success.keepCode': 'Keep this code: it is the only way to check the status of your request.',
  'success.copy': 'Copy code',
  'success.copied': 'Copied',
  'success.attachNow': 'Attach a photo or document',
  'success.attachDone': 'File attached.',
  'success.track': 'Track my request',
  'success.another': 'Submit another request',
  'track.heading': 'Track your request',
  'track.codePlaceholder': 'JS-XXXX-XXXX',
  'track.search': 'Check',
  'track.notFound': 'No request found with this code.',
  'track.invalidCode': 'The code must look like JS-XXXX-XXXX.',
  'track.submittedOn': 'Submitted on',
  'track.updatedOn': 'Last update',
  'track.timeline': 'History',
  'status.RECEIVED': 'Received',
  'status.IN_REVIEW': 'Under review',
  'status.ANSWERED': 'Answered',
  'status.CLOSED': 'Closed',
  'status.REJECTED': 'Not admissible',
  'statusHelp.RECEIVED': 'Your request has been recorded. The team will review it soon.',
  'statusHelp.IN_REVIEW': 'The project team is evaluating your request.',
  'statusHelp.ANSWERED': 'An answer has been provided.',
  'statusHelp.CLOSED': 'The request has been closed.',
  'statusHelp.REJECTED': 'The request is not admissible. See the reason given.',
  'category.CONSULTA': 'Question',
  'category.DUDA': 'Doubt',
  'category.SUGERENCIA': 'Suggestion',
  'category.QUEJA': 'Complaint',
  'category.RECLAMO': 'Grievance',
  'event.CREATED': 'Request received',
  'event.STATUS_CHANGED': 'Status change',
  'event.COMMENT': 'Message from the team',
  'event.ANSWER_SENT': 'Answer',
  'event.ATTACHMENT_ADDED': 'File attached',
  'event.DATA_DELETION_REQUESTED': 'Data deletion request recorded',
  'event.PII_PURGED': 'Personal data deleted',
  'track.deletion.title': 'Delete my personal data',
  'track.deletion.text':
    'If you left your details and want them deleted, enter your document number to confirm your identity. The team will process the request within 30 days.',
  'track.deletion.button': 'Request deletion',
  'track.deletion.done': 'Request recorded. If the data matches, it will be deleted within 30 days.',
  'privacy.heading': 'Personal data processing notice',
  'privacy.who': 'Controller: PUR, within the Jubilación Segura project.',
  'privacy.why': 'Purpose: receive, evaluate and answer your question, doubt, suggestion, complaint or grievance.',
  'privacy.what':
    'Data: only what you enter in the form (name, identity document, date of birth, contact, location) and the files you attach. Anonymous registration collects no personal data.',
  'privacy.how':
    'Protection: your data is stored encrypted on Microsoft Azure servers (European Union). Only the authorised project team can access it and every access is logged.',
  'privacy.howLong': 'Retention: your personal data is deleted {days} days after your request is closed.',
  'privacy.rights':
    'Rights: you may request access, rectification or deletion of your data from the tracking page using your code and document number, or by emailing the project.',
  'privacy.law': 'Legal basis: your consent (Peruvian Personal Data Protection Law No. 29733 and GDPR).',
  'admin.title': 'Back-office',
  'admin.signIn': 'Sign in with Microsoft',
  'admin.signInSub': 'Restricted to the project team',
  'admin.devLogin': 'Development access (no Microsoft)',
  'admin.signOut': 'Sign out',
  'admin.forbidden': 'Your account does not have access to this application.',
  'admin.dashboard': 'Dashboard',
  'admin.requests': 'Requests',
  'admin.all': 'All',
  'admin.search': 'Search (code or text)',
  'admin.anonymousOnly': 'Anonymous only',
  'admin.identifiedOnly': 'With details only',
  'admin.col.code': 'Code',
  'admin.col.date': 'Date',
  'admin.col.category': 'Type',
  'admin.col.province': 'Province',
  'admin.col.status': 'Status',
  'admin.col.type': 'Registration',
  'admin.empty': 'No requests match these filters.',
  'admin.detail': 'Request detail',
  'admin.requesterData': 'Requester details',
  'admin.piiWarning': 'Personal data — this access is recorded in the audit log.',
  'admin.purged': 'Personal data deleted on {date}.',
  'admin.attachments': 'Attachments',
  'admin.open': 'Open',
  'admin.timeline': 'History',
  'admin.publicEvent': 'Visible to the requester',
  'admin.changeStatus': 'Change status',
  'admin.addComment': 'Add message',
  'admin.message': 'Message',
  'admin.makePublic': 'Show to the requester on the tracking page',
  'admin.save': 'Save',
  'admin.saved': 'Saved',
  'admin.stats.total': 'Total',
  'admin.stats.byStatus': 'By status',
  'admin.stats.byCategory': 'By type',
  'admin.stats.byProvince': 'By province',
  'admin.stats.last30': 'Last 30 days',
  'admin.rejectionReason': 'Give the reason (visible to the requester).',
};

const DICTS: Record<Locale, UiStrings> = {
  es: { ...es, ...EVENT_STRINGS.es },
  fr: { ...fr, ...EVENT_STRINGS.fr },
  en: { ...en, ...EVENT_STRINGS.en },
};

export function uiText(lang: Locale, key: string, params?: Record<string, string | number>): string {
  const raw = DICTS[lang]?.[key] ?? DICTS[DEFAULT_LANGUAGE][key] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k: string) => String(params[k] ?? `{${k}}`));
}
